<template>
  <Listbox v-model="selected_item" as="div" :class="classes" class="relative z-20 bg-transparent">
    <ListboxButton as="template">
      <div class="flex rounded-md shadow-sm" :class="{ '!cursor-pointer': hyperlink }">
        <div class="relative flex items-stretch flex-grow">
          <div v-if="hyperlink" class="absolute z-10 inset-y-0 right-2.5 flex items-center" @click="handleLinkClick">
            <Link class="w-5 h-5 cursor-pointer" />
          </div>
          <div
            v-else-if="icon && $utils.isEmpty(search_term)"
            class="absolute z-10 inset-y-0 right-2.5 flex items-center pointer-events-none">
            <component :is="icon" class="w-5 h-5 text-common-grey" aria-hidden="true" />
          </div>
          <div
            v-show="search_term?.length > 0 && !disabled && showClearBtn"
            class="absolute inset-y-0 flex items-center justify-center right-1"
            @click="reset">
            <div
              class="flex items-center p-1 transition ease-in-out rounded-full cursor-pointer hover:bg-gray-300 duration-side-menu-transition-duration">
              <X class="w-5 h-5 text-gray-400" aria-hidden="true" />
            </div>
          </div>
          <TextInput
            v-model="search_term"
            :value="search_term"
            :disabled="disabled"
            :show-success-check-mark="hyperlink ? false : showSuccessCheckMark"
            :placeholder="translatedPlaceholder"
            :validate-on-input="true"
            :debounce="800"
            autocomplete="off"
            :name="name"
            :label="label"
            type="text"
            :keydown="
              ($event) => {
                if ($event.keyCode == 32) {
                  //prevent propagation to ListboxButton
                  $event.stopPropagation()
                }
              }
            "
            @update:modelValue="
              () => {
                $emit('changed', search_term)
                if (search_term) {
                  show_results = true
                }
              }
            " />
        </div>
      </div>
    </ListboxButton>

    <transition
      v-show="search_results.length > 0 && search_term?.length > 0 && show_results"
      leave-active-class="transition duration-100 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0">
      <ListboxOptions
        static
        class="absolute z-10 w-full mt-1 overflow-auto text-base bg-white rounded-lg shadow-lg max-h-60 dark:text-gray-300 dark:bg-dark-screen-background ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
        <ListboxOption v-for="item in search_results" v-slot="{ active }" :key="item" :value="item">
          <div
            :class="{ 'bg-light-brand-blue text-white': active }"
            class="relative block py-2 pl-8 pr-4 truncate transition ease-in-out rounded-lg cursor-pointer group hover:bg-light-brand-blue dark:hover:bg-dark-brand-blue duration-side-menu-transition-duration">
            {{ getRecordMetricValue(item) }}
            <span
              v-if="showPlusBtn"
              class="absolute inset-y-0 left-0 flex items-center pl-1.5 text-common-grey group-hover:text-white group-focus:text-white transition ease-in-out duration-side-menu-transition-duration">
              <Plus class="w-5 h-5" aria-hidden="true" />
            </span>
          </div>
        </ListboxOption>
      </ListboxOptions>
    </transition>
  </Listbox>
</template>

<script>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/vue"
import { Search, X, Plus, Link } from "lucide-vue-next"
import dataService from "@/services/data.service"
import TextInput from "./TextInput.vue"
import { Record } from "@/models/record"

export default {
  components: {
    Search,
    X,
    Plus,
    Listbox,
    ListboxOptions,
    ListboxOption,
    ListboxButton,
    TextInput,
    Link
  },
  props: {
    hyperlink: null,
    label: {
      type: String,
      required: false
    },

    value: {
      type: Object,
      required: false
    },
    name: {
      type: String,
      default: "name"
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    objectType: {
      type: String,
      required: false,
      default: "company"
    },

    classes: {
      type: String,
      default: ""
    },

    disabled: {
      type: Boolean,
      default: false
    },
    showSuccessCheckMark: {
      type: Boolean,
      default: true
    },
    showClearBtn: {
      type: Boolean,
      default: true
    },
    showPlusBtn: {
      type: Boolean,
      default: true
    },

    icon: {
      type: [Object, Function], // Allow both object and function components
      default: () => Search
    },

    filterMetricIds: {
      type: Array,
      required: true
    },
    ownersFilter: {
      type: Array,
      default: null
    }
  },
  emits: ["chosen", "changed", "selectedObject", "reset"],

  data() {
    return {
      show_results: false,
      search_term: null,
      search_results: [],
      selected_item: null
    }
  },
  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    }
  },
  watch: {
    search_term() {
      this.getAsyncResults()
    },

    selected_item() {
      if (this.selected_item) {
        this.show_results = false
        this.search_results = []

        this.$emit("chosen", this.selected_item)

        this.search_term = this.getRecordMetricValue(this.selected_item)

        // this.selected_item = null
      }
    }
  },
  created() {
    if (this.value?.name) {
      this.search_term = this.value?.name
    } else if (this.value) {
      this.$store
        .dispatch("data/getRecord", this.value)
        .then((res) => {
          this.search_term = this.$utils.clone(res.data?.name)
        })
        .catch(() => {
          this.search_term = this.value
        })
    } else {
      this.search_term = ""
    }

    document.addEventListener("mousedown", () => {
      this.show_results = false
    })
  },

  methods: {
    handleLinkClick() {
      this.hyperlink()
    },
    getAsyncResults() {
      let filters = { filter: "" }
      if (this.search_term != null) {
        filters["filter"] = ["("]
        this.filterMetricIds.forEach((id) =>
          filters["filter"].push(
            ...[this.$utils.getMetricById(id).reference_name, "in", `'${this.$utils.escapeApostrophe(this.search_term)}'`, "or"]
          )
        )
        filters["filter"].pop()
        filters["filter"].push(")")
        filters["filter"] = filters["filter"].join(" ")
      }
      filters["object_types"] = [this.objectType]
      filters["owners"] = this.ownersFilter
      if (filters.filter.split(" ").length > 0) {
        dataService.filterRecords(filters).then((result) => {
          // this.search_is_on = false
          //let companies = result.data.results.filter((record) => record.type == "company")
          this.search_results = result.data.results
        })
      } else {
        this.show_results = false
      }
    },
    reset() {
      this.search_term = null
      this.search_results = []
      this.show_results = false
      this.$emit("reset")
    },
    getRecordMetricValue(record) {
      return Record.from(record).name
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
